import { useMemo, useState } from "react";
import {
  MDSIconKind,
  MDSMenuDropdown,
  MDSMenuDropdownSlotName,
  MDSSelectMenu,
  MDSSelectMenuItem,
  MDSMenuItemVariant,
  MDSMenuItemSelectionVariant,
  MDSMenuItemHeader,
  MDSToggleButton,
} from "@/mds";
import { pluralize } from "@/modules/pluralize";
import { NoteMediaKind } from "@/store/note/types";
import {
  MediaKindsFacetFilterItem,
  MediaKindsFacetFilterItemKind,
} from "./media-kinds-facet-filter.types";

interface MediaKindsFacetFilterProps {
  items: MediaKindsFacetFilterItem[];
  onSelectItem: (id: NoteMediaKind) => void;
  onSelectClear: () => void;
  selectedItems: NoteMediaKind[];
}

export const MediaKindsFacetFilter = ({
  items,
  onSelectItem,
  onSelectClear,
}: MediaKindsFacetFilterProps) => {
  const [isOpen, setIsOpen] = useState(false);
  const selectedItems = items.filter(item => item.isSelected);
  const hasSelectedItems = selectedItems.length > 0;

  const computedButtonLabel = useMemo(() => {
    const count = selectedItems.length;

    if (selectedItems.length === 1) {
      return `Contains ${selectedItems[0].label.toLowerCase()}`;
    }

    if (count > 1) {
      return `Contains ${selectedItems[0].label.toLowerCase()} or ${count - 1} ${pluralize(
        count - 1,
        "other",
        "others"
      )}`;
    }

    return "Contains";
  }, [selectedItems]);

  return (
    <MDSMenuDropdown data-test-id="media-kinds-facet-filter" onOpenChange={setIsOpen}>
      <MDSToggleButton
        slot={MDSMenuDropdownSlotName.Trigger}
        isOpen={isOpen}
        iconKind={MDSIconKind.Shapes}
        isToggled={hasSelectedItems}
        label={computedButtonLabel}
        onClear={() => onSelectClear()}
      />
      <MDSSelectMenu
        slot={MDSMenuDropdownSlotName.Menu}
        aria-label="Filter by media kinds"
        selectionBehavior="toggle"
        selectionMode="single"
        shouldFocusWrap={true}
        style={{ width: 200 }}
      >
        {items.map(item => {
          if (item.kind === MediaKindsFacetFilterItemKind.Label) {
            return <MDSMenuItemHeader key={item.id} label={item.label} />;
          }

          if (item.kind === MediaKindsFacetFilterItemKind.MediaKind) {
            return (
              <MDSSelectMenuItem
                key={item.id}
                id={item.id}
                label={item.label}
                icon={item.data.icon}
                isSelected={!!item.isSelected}
                onClick={() => onSelectItem(item.data.mediaKind)}
                variant={MDSMenuItemVariant.OneLine}
                selectionVariant={MDSMenuItemSelectionVariant.RadioButton}
              />
            );
          }
        })}
      </MDSSelectMenu>
    </MDSMenuDropdown>
  );
};
